import { Node_Type } from './../ricos-schema/legacy/wix/rich_content/v1';
import type {
  GeneralContext,
  KeyboardShortcut,
  LocalizedDisplayData,
  Platform,
  ShortcutDataProvider,
  TFunction,
} from './../ricos-types';

export class ShortcutsSheet {
  private readonly ordering: Record<
    KeyboardShortcut['group'],
    (KeyboardShortcut['name'] | KeyboardShortcut['name'][])[]
  > = {
    ShortcutsSheet_Section_General: [
      'SelectPrevBlock',
      'MoveSelectionThroughElements',
      'UNDO',
      'REDO',
      'OpenBlocksMenu',
      'OpenShortcutsSheet',
      'SelectAll',
      'SelectTextBlocks',
      'ClearSelection',
      'MoveSelectedBlockUp',
      'MoveSelectedBlockDown',
    ],
    ShortcutsSheet_Section_ToolbarNavigation: [
      'EnterToolbar',
      'MoveSelectionToNextOption',
      'MoveSelectionToPreviousOption',
      'ApplySelection',
      'ExitToolbar',
    ],
    ShortcutsSheet_Section_Formatting: [
      'PARAGRAPH',
      `${Node_Type.HEADING}.1`,
      `${Node_Type.HEADING}.2`,
      `${Node_Type.HEADING}.3`,
      `${Node_Type.HEADING}.4`,
      `${Node_Type.HEADING}.5`,
      `${Node_Type.HEADING}.6`,
      'FONT_SIZE.increase',
      'FONT_SIZE.decrease',
      'INDENT.increase',
      'INDENT.decrease',
      'TextAlignment.LEFT',
      'TextAlignment.RIGHT',
      'TextAlignment.CENTER',
      'TextAlignment.JUSTIFY',
      'BOLD',
      'ITALIC',
      'UNDERLINE',
      'ORDERED_LIST',
      'BULLETED_LIST',
      'BLOCKQUOTE',
      'LINK',
      'CODE_BLOCK',
      'PasteWithoutFormatting',
      'NewLine',
      'SoftNewLine',
      'Enter',
    ],
    ShortcutsSheet_Section_BlocksMenu: [
      'OpenBlocksMenu',
      ['SelectNextBlockWithTab', 'SelectNextBlockArrows'],
      // TODO ['SelectPrevBlockWithTab', 'SelectPrevBlockArrows'],
      'ApplySelection',
      'ExitBlocksMenu',
      'MoveSelectedBlockUp',
      'MoveSelectedBlockDown',
    ],
    ShortcutsSheet_Section_Table: [
      ['MoveToNextCellTab', 'MoveToNextCellArrow'],
      ['MoveToPreviousCellTab', 'MoveToPreviousCellArrow'],
      'EnterExitCell',
      ['AddNewLineShift', 'AddNewLineAlt'],
      'DropLineInList',
      'SelectContentInCell',
      'SelectColumn',
      'SelectRow',
      'SelectTable',
      'AddNewColumnRow',
      'DeleteColumnRow',
    ],
    ShortcutsSheet_Section_CollapsibleList: [
      ['MoveToNextElementTab', 'MoveToNextElementEnter'],
      // TODO: add SelectAllContentWithinSection
      'SelectEntireList',
    ],
  };

  private readonly shortcuts: ShortcutDataProvider;

  private readonly translator: TFunction;

  private readonly platform: Platform;

  constructor(shortcuts: ShortcutDataProvider, context: GeneralContext) {
    this.shortcuts = shortcuts;
    this.translator = context.t;
    this.platform = context.platform;
  }

  private getSections() {
    const displayData = this.shortcuts.getDisplayData(this.translator, this.platform);
    let sections = [
      'ShortcutsSheet_Section_General',
      'ShortcutsSheet_Section_ToolbarNavigation',
      'ShortcutsSheet_Section_Formatting',
      'ShortcutsSheet_Section_BlocksMenu',
    ];
    if (displayData.ShortcutsSheet_Section_Table) {
      sections = sections.concat('ShortcutsSheet_Section_Table');
    }
    if (displayData.ShortcutsSheet_Section_CollapsibleList) {
      sections = sections.concat('ShortcutsSheet_Section_CollapsibleList');
    }
    return sections;
  }

  getDisplayData(): Record<KeyboardShortcut['group'], LocalizedDisplayData[]> {
    return this.getSections().reduce((map, sectionId) => {
      return {
        ...map,
        [sectionId]: this.ordering[sectionId].map(id => {
          if (Array.isArray(id)) {
            const displayDataItems = id.map(id =>
              this.shortcuts.getShortcutDisplayData(id, this.translator, this.platform)
            );
            return {
              ...displayDataItems[0],
              keyCombinationText: displayDataItems.map(
                ({ keyCombinationText }) => keyCombinationText
              ),
            };
          }
          return this.shortcuts.getShortcutDisplayData(id, this.translator, this.platform);
        }),
      };
    }, {});
  }
}
